import type { Query } from './apiTypes'
import type { WorldFlowEnvelope } from './worldFlowTypes'
import type { AnalysisSearchEnvelope, SearchSliceEnvelope } from './searchContract'
import type { WindowTraceResult } from './windowTraceTypes'
import { apiUrl } from './endpoints'
import { authFetch } from '@/auth/authFetch'

async function postJSON<TReq, TRes>(path: string, body: TReq, signal?: AbortSignal): Promise<TRes> {
  const res = await authFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status} ${res.statusText}${text ? `: ${text}` : ''}`)
  }
  return await res.json()
}

async function getJSON<TRes>(path: string, signal?: AbortSignal): Promise<TRes> {
  const res = await authFetch(path, { signal })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status} ${res.statusText}${text ? `: ${text}` : ''}`)
  }
  return await res.json()
}

/**
 * REST client. Every endpoint accepts the same `Query` envelope; the chosen
 * endpoint just signals which response shape to return.
 *
 *  - `/api/dimension` → latest dimension aggregates (one row per dimension value)
 *  - `/api/stream`    → latest per-stream slices (within `query.slice` scope)
 *  - `/api/analysis`  → timeseries for one slice (set `filters.streamId` for a
 *                       stream deep-dive, or `slice.value` for a dimension value)
 */
export const apiClient = {
  dimensionSearch: (query: Query, signal?: AbortSignal) =>
    postJSON<Query, SearchSliceEnvelope>(apiUrl('/api/dimension'), { ...query, view: 'dimension' }, signal),

  streamSearch: (query: Query, signal?: AbortSignal) =>
    postJSON<Query, SearchSliceEnvelope>(apiUrl('/api/stream'), { ...query, view: 'stream' }, signal),

  analysisSearch: (query: Query, signal?: AbortSignal) =>
    postJSON<Query, AnalysisSearchEnvelope>(apiUrl('/api/analysis'), { ...query, view: 'analysis' }, signal),

  worldFlow: (
    query: Pick<Query, 'time' | 'filters' | 'clientGrain' | 'includeOffline'>,
    signal?: AbortSignal,
  ) =>
    postJSON<typeof query, WorldFlowEnvelope>(apiUrl('/api/world'), query, signal),

  windowTrace: (
    opts: { tickTs: number; streamId?: string | null },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams({ tickTs: String(opts.tickTs) })
    if (opts.streamId) q.set('streamId', opts.streamId)
    return getJSON<WindowTraceResult>(apiUrl(`/api/debug/window-trace?${q}`), signal)
  },
}
